export interface DieselSurchargeConfig {
  dieselSurchargeEnabled?: boolean;
  hasCustomDieselSurcharge?: boolean;
  dieselSurchargePercentage?: number;
}

/** Seeded / conventional product type id used when settings omit a diesel product. */
export const FALLBACK_DIESEL_SURCHARGE_PRODUCT_TYPE_ID = 'DIE';

/**
 * Effective diesel surcharge percentage for a customer or carrier, as an int
 * with 2 decimals (500 = 5%). Parties without their own percentage fall back
 * to the company-wide default from the general settings.
 */
export function getEffectiveDieselSurchargePercentage(
  party: DieselSurchargeConfig | null | undefined,
  defaultPercentage: number,
): number {
  if (!party?.dieselSurchargeEnabled) {
    return 0;
  }
  return party.hasCustomDieselSurcharge ? (party.dieselSurchargePercentage ?? 0) : defaultPercentage;
}

/** Surcharge in cents for a base amount in cents, given a percentage (500 = 5%). */
export function calculateDieselSurchargeCents(baseCents: number, percentage: number): number {
  return Math.round((baseCents * percentage) / 10000);
}

/** Configured diesel product type, or DIE when general settings have none. */
export function resolveDieselSurchargeProductTypeId(configuredId: string | null | undefined): string {
  return configuredId || FALLBACK_DIESEL_SURCHARGE_PRODUCT_TYPE_ID;
}

export function isDieselSurchargeProductType(
  productTypeId: string | null | undefined,
  configuredId: string | null | undefined,
): boolean {
  if (!productTypeId) {
    return false;
  }
  return productTypeId === resolveDieselSurchargeProductTypeId(configuredId);
}

export interface DieselSurchargeLine {
  productTypeId: string;
  amount: number;
  unitPrice: number;
}

/**
 * Line the surcharge is calculated from: configured base product, else the
 * default transport product, else the first non-diesel line.
 */
export function findDieselSurchargeBaseLine<T extends DieselSurchargeLine>(
  lines: T[],
  options: {
    baseProductTypeId?: string | null;
    defaultProductTypeId?: string | null;
    dieselProductTypeId?: string | null;
  },
): T | undefined {
  const dieselId = resolveDieselSurchargeProductTypeId(options.dieselProductTypeId);
  if (options.baseProductTypeId) {
    const base = lines.find((line) => line.productTypeId === options.baseProductTypeId);
    if (base) {
      return base;
    }
  }
  if (options.defaultProductTypeId) {
    const fallback = lines.find((line) => line.productTypeId === options.defaultProductTypeId);
    if (fallback) {
      return fallback;
    }
  }
  return lines.find((line) => line.productTypeId !== dieselId);
}

/** Surcharge unit price in cents from a priced line and percentage (500 = 5%). */
export function calculateDieselSurchargeFromLine(
  baseLine: Pick<DieselSurchargeLine, 'amount' | 'unitPrice'>,
  percentage: number,
): number {
  const baseCents = (baseLine.amount / 100) * baseLine.unitPrice;
  return calculateDieselSurchargeCents(baseCents, percentage);
}
